<script setup lang="ts">
const { locale, setLocale, t } = useI18n()
const isMounted = shallowRef(false)
const activeLocale = computed<'es' | 'en'>(() => (locale.value === 'en' ? 'en' : 'es'))
const options = computed(() => [
  { value: 'es' as const, label: t('locale.esShort') },
  { value: 'en' as const, label: t('locale.enShort') }
])

onMounted(() => {
  isMounted.value = true
})
</script>

<template>
  <div class="locale-switcher" role="group" :aria-label="t('locale.label')">
    <button
      v-for="option in options"
      :key="option.value"
      class="locale-switcher__option"
      :class="{ 'locale-switcher__option--active': activeLocale === option.value }"
      type="button"
      :disabled="!isMounted"
      :aria-pressed="activeLocale === option.value"
      @click="setLocale(option.value)"
    >
      {{ option.label }}
    </button>
  </div>
</template>

<style scoped>
.locale-switcher {
  display: inline-flex;
  align-items: center;
  height: 2.8rem;
  border: 1px solid var(--tq-border);
  border-radius: var(--tq-radius-pill);
  background: color-mix(in oklch, var(--tq-bg-muted) 82%, var(--tq-bg-elevated));
  padding: 0.2rem;
}

.locale-switcher__option {
  display: inline-flex;
  min-width: 3rem;
  height: 2.32rem;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: var(--tq-radius-pill);
  background: transparent;
  color: var(--tq-text-muted);
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  transition: background 180ms var(--tq-ease-out), color 180ms var(--tq-ease-out), box-shadow 180ms var(--tq-ease-out);
}

.locale-switcher__option:disabled {
  cursor: wait;
  opacity: 0.65;
}

.locale-switcher__option--active {
  background: var(--tq-bg-elevated);
  color: var(--tq-accent-readable);
  box-shadow: var(--tq-shadow-tight);
}
</style>
